'use client';

import Accordion from '@blocks/Accordion';
import { Loader } from '@ngg/components';
import { StarIcon } from '@ngg/icons';
import { Suspense } from 'react';
import type { Dictionary } from 'types/common';
import type { TReviewsData } from 'types/testFreaks';

import type { MarketConfiguration } from '@/config/markets/const';
import { cn } from '@/lib/utils';
import { scrollToElementById } from '@/utils/windowScroll';

import ReviewContent from './reviewContent';

type TReviewAccordion = {
    reviews: TReviewsData | null;
    dictionary: Dictionary;
    className?: string;
    title: string;
    productId: string;
    marketConfig: MarketConfiguration;
};

export function ReviewScore({
    score,
    count,
    isSmall,
}: {
    score: number | undefined;
    count?: number | undefined;
    isSmall?: boolean;
}) {
    if (!score) return null;

    return (
        <div
            className="flex cursor-pointer items-center"
            onClick={() => scrollToElementById('product-reviews', -105)}>
            <div className={cn('mr-1 flex text-xl', isSmall && 'text-lg')}>
                {[...Array(5)].map((_, i) => (
                    <StarIcon
                        // eslint-disable-next-line react/no-array-index-key
                        key={i}
                        filled={i < (score || 0)}
                    />
                ))}
            </div>
            {count ? <p className="text-sm">({count})</p> : null}
        </div>
    );
}

async function ReviewAccordion({
    reviews,
    dictionary,
    className,
    productId,
    title: _title,
    marketConfig,
}: TReviewAccordion) {
    const title = reviews?.summary?.your_review_count
        ? `${_title} (${reviews?.summary?.your_review_count})`
        : _title;

    return (
        <Accordion
            title={title}
            transitionProps={{ appear: false }}
            classNames={{
                wrapper: className,
                panel: 'rounded-none mt-0 space-y-5',
            }}>
            <div>
                {reviews?.summary?.your_avg_score ? (
                    <div className="flex items-center border-b border-grey-150 pb-4 text-base">
                        <ReviewScore score={reviews?.summary?.your_avg_score} />

                        <p>
                            {reviews?.summary?.your_avg_score}{' '}
                            {dictionary?.basedOn?.toLowerCase()}{' '}
                            {reviews?.summary?.your_review_count}{' '}
                            {dictionary?.reviews?.toLowerCase()}
                        </p>
                    </div>
                ) : null}

                <ReviewContent
                    marketConfig={marketConfig}
                    initialReviews={reviews}
                    dictionary={dictionary}
                    productId={productId}
                />
            </div>
        </Accordion>
    );
}

function Skeleton({ className, title }: Omit<TReviewAccordion, 'productId'>) {
    return (
        <Accordion
            title={title}
            transitionProps={{ appear: false }}
            classNames={{
                wrapper: className,
                panel: 'rounded-none mt-0 space-y-5',
            }}>
            <Loader />
        </Accordion>
    );
}

export default function ReviewAccordionSuspense(props: TReviewAccordion) {
    return (
        <Suspense fallback={<Skeleton {...props} />}>
            <ReviewAccordion {...props} />
        </Suspense>
    );
}
